import { useCallback } from 'react';
import { useChatStore } from '../store/chat.store';
import { apiFetch, type Attachment } from '../lib/api';
import {
  consumeStream,
  beginTurn,
  endTurn,
  abortActiveTurn,
} from '../lib/chat-stream';

const RECONNECT_WINDOW_MS = 3000;

export function useStream() {
  const finalizeMessage = useChatStore((s) => s.finalizeMessage);
  const resumeStreamingMessage = useChatStore((s) => s.resumeStreamingMessage);

  const stopStream = useCallback(() => abortActiveTurn(), []);

  const sendMessage = useCallback(
    async (
      conversationId: string,
      content: string,
      attachments: Attachment[] = [],
    ) => {
      const controller = beginTurn();

      const turnIdRef = { current: '' };
      const connectStart = Date.now();

      try {
        const res = await apiFetch('/api/chat/messages', {
          method: 'POST',
          body: JSON.stringify({ conversationId, content, attachments }),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) return;

        async function tryConnect(
          body: ReadableStream<Uint8Array> | null,
        ): Promise<void> {
          try {
            let stream = body;
            if (!stream) {
              const r = await apiFetch(
                `/api/chat/turns/${turnIdRef.current}/stream`,
                { signal: controller.signal },
              );
              if (!r.ok || !r.body) throw new Error('reconnect failed');
              stream = r.body;
            }
            await consumeStream(stream, turnIdRef, conversationId);
          } catch {
            // A user-initiated stop is not a dropped connection — never reconnect.
            if (controller.signal.aborted) {
              if (turnIdRef.current)
                finalizeMessage(turnIdRef.current, 'cancelled');
              return;
            }
            if (
              Date.now() - connectStart < RECONNECT_WINDOW_MS &&
              turnIdRef.current
            ) {
              await new Promise((r) => setTimeout(r, 1000));
              return tryConnect(null);
            }
            if (turnIdRef.current)
              finalizeMessage(turnIdRef.current, 'disconnected');
          }
        }
        await tryConnect(res.body);
      } catch (err) {
        // Aborting before the response lands rejects the POST itself.
        if (!controller.signal.aborted) throw err;
        if (turnIdRef.current) finalizeMessage(turnIdRef.current, 'cancelled');
      } finally {
        endTurn(controller);
      }
    },
    [finalizeMessage],
  );

  const retryTurn = useCallback(
    async (turnId: string) => {
      resumeStreamingMessage(turnId);
      const turnIdRef = { current: turnId };
      const connectStart = Date.now();
      async function tryConnect(): Promise<void> {
        try {
          const res = await apiFetch(`/api/chat/turns/${turnId}/stream`);
          if (!res.ok || !res.body) throw new Error('stream unavailable');
          await consumeStream(
            res.body,
            turnIdRef,
            useChatStore.getState().activeConversationId ?? '',
          );
        } catch {
          if (Date.now() - connectStart < RECONNECT_WINDOW_MS) {
            await new Promise((r) => setTimeout(r, 1000));
            return tryConnect();
          }
          finalizeMessage(turnId, 'disconnected');
        }
      }
      await tryConnect();
    },
    [resumeStreamingMessage, finalizeMessage],
  );

  return { sendMessage, retryTurn, stopStream };
}
